/**
 * A-Block A0 — canonical SPA route builders for the holding-first UX.
 *
 * Mirrors the prototype `routes` object in KormaxClient360_v1_3.jsx and the
 * frontend_routes contract in A_Block_Build_TZ_v1_2.md §2. ALL client routes are
 * child-nested under /clients/:holdingId (AC-A0-01); there is NO root-level
 * legal-entity route (AC-A0-03). Standalone synthetic clients reuse the same
 * /clients/:holdingId shape (AC-A0-02) — "standalone" is backend data, not a
 * distinct route.
 *
 * A1–A4 import these so link targets never drift from the route table.
 */
export const clientRoutes = {
  /** List of holdings + standalone clients. */
  list: (): string => "/clients",

  /** Client360 (holding card). Works for real and synthetic holdings. */
  client: (holdingId: string): string => `/clients/${holdingId}`,

  /** Юрособи tab (list of member legal entities). */
  entities: (holdingId: string): string => `/clients/${holdingId}/legal-entities`,

  /** Legal-entity detail — ONLY reachable nested under its holding. */
  legalEntity: (holdingId: string, accountId: string): string =>
    `/clients/${holdingId}/legal-entities/${accountId}`,

  /** «Структура» tree (Этап 4) — Юрособи→Точки→Контакти under the holding. */
  structure: (holdingId: string): string => `/clients/${holdingId}/structure`,

  /** «Точки» tab (Этап 5) — holding outlets table + address-picker. */
  outlets: (holdingId: string): string => `/clients/${holdingId}/outlets`,

  /** Контакти tab (holding-master aggregate). */
  contacts: (holdingId: string): string => `/clients/${holdingId}/contacts`,

  /** Договори tab (list). */
  contracts: (holdingId: string): string => `/clients/${holdingId}/contracts`,

  /** Активності tab (read-only timeline · 3A). Optional deep-link action. */
  activities: (holdingId: string, action?: "task_create" | "follow_up" | "call_log"): string =>
    `/clients/${holdingId}/activities` + (action ? `?action=${action}` : ""),

  /** Задачі tab (open tasks · 3A). */
  tasks: (holdingId: string): string => `/clients/${holdingId}/tasks`,

  /** Рекламації tab (list). */
  complaints: (holdingId: string): string => `/clients/${holdingId}/complaints`,

  /** D-146 W2 — Матеріали (10th tab, holding-grain). */
  materials: (holdingId: string): string => `/clients/${holdingId}/materials`,

  /** Existing contract within a holding. */
  contract: (holdingId: string, contractId: string): string =>
    `/clients/${holdingId}/contracts/${contractId}`,

  /** New contract; legalEntity query is optional context (org variant uses it). */
  contractNew: (holdingId: string, accountId?: string): string =>
    `/clients/${holdingId}/contracts/new` +
    (accountId ? `?legalEntity=${accountId}` : ""),

  /** Existing activity (meeting/call/task) within a holding. */
  activity: (holdingId: string, activityId: string): string =>
    `/clients/${holdingId}/activity/${activityId}`,

  /** New activity; kind query is optional (meeting|call|task). */
  activityNew: (holdingId: string, kind?: string): string =>
    `/clients/${holdingId}/activity/new` + (kind ? `?kind=${kind}` : ""),

  /** Профіль surface tab (Этап 6 type-aware renderer over 3.0A-4). */
  profile: (holdingId: string): string =>
    `/clients/${holdingId}/profile`,

  /**
   * Business-profile QUICK FORM (3.0A-4 scenario/JSON editor). The Этап 6 Профіль
   * surface delegates scenario editing here ("Редагувати/Заповнити").
   */
  businessProfile: (holdingId: string): string =>
    `/clients/${holdingId}/business-profile`,

  /**
   * A4 RE-ORIENTATION — legacy account anchor. Feature CTAs (tasks, contracts,
   * meetings) carry an accountId but not a holdingId; this funnels through the
   * account-anchor resolver, which lands on the holding-grain Client360 (default)
   * or a specific tab via `to`. AC-A4-01 (CTA→Client360) · AC-A4-02 (holding-grain
   * default). Reachable tabs: overview | activities | contracts | complaints |
   * profile | entities | legal-entity (forces the LE detail sub-page).
   */
  accountAnchor: (accountId: string, to?: AccountAnchorTarget): string =>
    `/accounts/${accountId}` + (to ? `?to=${to}` : ""),
} as const;

export type AccountAnchorTarget =
  | "overview"
  | "activities"
  | "contracts"
  | "complaints"
  | "profile"
  | "entities"
  | "legal-entity";

/** Build the resolved Client360 path for an anchor target + resolved ids. */
export function anchorTargetPath(
  holdingId: string,
  accountId: string,
  to?: AccountAnchorTarget | null,
): string {
  switch (to) {
    case "activities":
      return clientRoutes.activities(holdingId);
    case "contracts":
      return clientRoutes.contracts(holdingId);
    case "complaints":
      return clientRoutes.complaints(holdingId);
    case "profile":
      return clientRoutes.profile(holdingId);
    case "entities":
      return clientRoutes.entities(holdingId);
    case "legal-entity":
      return clientRoutes.legalEntity(holdingId, accountId);
    case "overview":
      return clientRoutes.client(holdingId);
    default:
      // AC-A4-02 — holding-grain default. A raw account reference now lands on the
      // holding card (Client360), with the legal entity as sub-context (Юрособи tab).
      return clientRoutes.client(holdingId);
  }
}

/** Client360 tab keys + labels (from VISUAL_APPROVAL_Client360_v1_1 bridge). */
export const CLIENT360_TABS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "overview", label: "Огляд" },
  { key: "entities", label: "Юрособи" },
  { key: "contracts", label: "Договори" },
  { key: "activities", label: "Активності" },
  { key: "tasks", label: "Задачі" },
  { key: "complaints", label: "Рекламації" },
  { key: "profile", label: "Профіль" },
];
